import { lazy, Suspense, type ReactNode, type ReactEventHandler } from "react";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import i18n from "../language/i18n";

export type StorageFileType = "PDF" | "IMAGE" | "AUDIO" | "VIDEO";

type AcceptedFormat =
  | "application/pdf"
  | "image/jpeg"
  | "image/png"
  | "image/jpg"
  | "image/webp"
  | "audio/mp3"
  | "video/mp4"
  | "video/webm"
  | "video/quicktime"
  | "video/x-msvideo";

type FileExtension =
  | "pdf"
  | "jpeg"
  | "png"
  | "jpg"
  | "webp"
  | "mp3"
  | "mp4"
  | "webm"
  | "mov"
  | "avi";

export const convertDataURLtoFile = (
  dataurl: string,
  filename: string,
): File => {
  const arr = dataurl.split(",");
  const mime = (arr[0].match(/:(.*?);/) || [])[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const retrieveFileTypeFromAcceptedFormat = (
  format: string,
): StorageFileType | undefined => {
  if (format === "application/pdf") return "PDF";
  if (
    format === "image/jpeg" ||
    format === "image/png" ||
    format === "image/jpg" ||
    format === "image/webp"
  ) {
    return "IMAGE";
  }
  if (format === "audio/mp3") return "AUDIO";
  if (
    format === "video/mp4" ||
    format === "video/webm" ||
    format === "video/quicktime" ||
    format === "video/x-msvideo"
  ) {
    return "VIDEO";
  }
  return undefined;
};

export const retrieveFileTypeFromExtension = (
  format: string,
): StorageFileType | "" => {
  if (format === "pdf") return "PDF";
  if (
    format === "jpeg" ||
    format === "png" ||
    format === "jpg" ||
    format === "webp"
  ) {
    return "IMAGE";
  }
  // mp3 alone was too narrow. A recorded answer from an iPhone arrives as .m4a and a desktop
  // recorder commonly produces .wav or .ogg, so those were typed "" and fell back to whatever the
  // caller had declared — which is the wrong-element-for-the-file case this sniffer exists to stop.
  // webm is deliberately NOT here: it is a container that is far more often video, and it is
  // already claimed below.
  if (
    format === "mp3" ||
    format === "m4a" ||
    format === "wav" ||
    format === "ogg" ||
    format === "aac"
  ) {
    return "AUDIO";
  }
  if (
    format === "mp4" ||
    format === "webm" ||
    format === "mov" ||
    format === "avi"
  ) {
    return "VIDEO";
  }
  return "";
};

// The storage file type of an answer upload: the mime first, then the file extension. Phones
// often report an empty or unlisted mime (HEIC, "" from some Android pickers), and the phone QR
// screen used to call `.toLowerCase()` on the undefined result — an unhandled TypeError that left
// the learner on "yükleniyor" forever. `undefined` means "not an uploadable type"; callers decide
// what to do with it (fail with a message, or the in-app "FILE" fallback).
export const resolveUploadFileType = (
  file: { type?: string; name?: string } | null | undefined,
): StorageFileType | undefined => {
  if (!file) return undefined;
  const byMime = retrieveFileTypeFromAcceptedFormat(
    (file.type || "").toLowerCase(),
  );
  if (byMime) return byMime;
  const name = file.name || "";
  const extension = name.includes(".")
    ? name.split(".").pop().toLowerCase()
    : "";
  return retrieveFileTypeFromExtension(extension) || undefined;
};

export const ANSWER_IMAGE_MAX_DIMENSION = 1200;
export const ANSWER_IMAGE_QUALITY = 0.7;
// Low-memory mobile Safari can leave a huge photo's <img> without ever firing onload/onerror;
// past this the original file is uploaded instead of waiting forever.
export const ANSWER_IMAGE_COMPRESSION_TIMEOUT_MS = 15000;

const COMPRESSIBLE_OUTPUT_TYPE_LIST = ["image/jpeg", "image/png", "image/webp"];

// Downscale an open-ended answer photo (longest edge ≤ 1200px, quality 0.7) before upload. Shared
// by the in-app upload and the phone QR screen. NEVER rejects: any failure — undecodable image,
// null blob, or no answer within `timeoutMs` — resolves to the ORIGINAL file, so compression can
// only ever make an upload smaller, never block it. A source the canvas cannot re-encode in its own
// format (HEIC, GIF, "image/jpg") comes out as JPEG with a .jpg name.
export const compressAnswerImageFile = (
  file: File,
  timeoutMs: number = ANSWER_IMAGE_COMPRESSION_TIMEOUT_MS,
): Promise<File> => {
  const type = ((file && file.type) || "").toLowerCase();
  if (!file || !type.startsWith("image/") || type === "image/svg+xml") {
    return Promise.resolve(file);
  }
  const outputType = COMPRESSIBLE_OUTPUT_TYPE_LIST.includes(type)
    ? type
    : "image/jpeg";
  const outputName =
    outputType === type
      ? file.name
      : `${(file.name || "image").replace(/\.[^.]+$/, "") || "image"}.jpg`;

  return new Promise<File>((resolve) => {
    let settled = false;
    const finish = (result: File) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => finish(file), timeoutMs);

    try {
      const reader = new FileReader();
      reader.onerror = () => finish(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.onerror = () => finish(file);
        img.onload = () => {
          try {
            let width = img.width;
            let height = img.height;
            if (width > height) {
              if (width > ANSWER_IMAGE_MAX_DIMENSION) {
                height = Math.round(
                  (height * ANSWER_IMAGE_MAX_DIMENSION) / width,
                );
                width = ANSWER_IMAGE_MAX_DIMENSION;
              }
            } else if (height > ANSWER_IMAGE_MAX_DIMENSION) {
              width = Math.round((width * ANSWER_IMAGE_MAX_DIMENSION) / height);
              height = ANSWER_IMAGE_MAX_DIMENSION;
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            if (!context) {
              finish(file);
              return;
            }
            context.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => {
                // Keep the original when the browser silently encoded another type (Safari < 17
                // returns PNG for WebP) or the re-encode came out no smaller.
                finish(
                  blob && blob.type === outputType && blob.size < file.size
                    ? new File([blob], outputName, { type: outputType })
                    : file,
                );
              },
              outputType,
              ANSWER_IMAGE_QUALITY,
            );
          } catch {
            finish(file);
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } catch {
      finish(file);
    }
  });
};

export interface IFileTypeOption {
  text: string;
  value: StorageFileType;
}

export type FileTypeOptionListVariant = "IMAGE_ONLY" | "PDF_ONLY" | "ACTIVITY";

export const retrieveFileTypeOptionList = (
  optionType?: FileTypeOptionListVariant,
): IFileTypeOption[] => {
  if (optionType === "IMAGE_ONLY") {
    return [{ text: i18n.t("IMAGE"), value: "IMAGE" }];
  }
  if (optionType === "PDF_ONLY") {
    return [{ text: i18n.t("PDF"), value: "PDF" }];
  }
  if (optionType === "ACTIVITY") {
    return [
      { text: i18n.t("VIDEO"), value: "VIDEO" },
      { text: i18n.t("AUDIO"), value: "AUDIO" },
      { text: i18n.t("IMAGE"), value: "IMAGE" },
    ];
  }
  return [
    { text: i18n.t("VIDEO"), value: "VIDEO" },
    { text: i18n.t("AUDIO"), value: "AUDIO" },
    { text: i18n.t("IMAGE"), value: "IMAGE" },
    { text: i18n.t("PDF"), value: "PDF" },
  ];
};

export const retrieveAcceptedFormats = (
  fileType: StorageFileType,
): string | undefined => {
  if (fileType === "IMAGE") {
    return "image/jpeg, image/png, image/jpg, image/webp";
  }
  if (fileType === "VIDEO") {
    return "video/mp4, video/webm, video/quicktime, video/x-msvideo";
  }
  if (fileType === "AUDIO") return "audio/mp3";
  if (fileType === "PDF") return "application/pdf";
  return undefined;
};

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

// Lazy so the react-pdf stack (pdfjs) loads only when a PDF is actually shown — this utility is
// imported by dashboard-adjacent components (announcements, chat, players) that rarely render one.
const PDFPreview = lazy(() => import("../components/pdf/PDFPreview"));

export const getBrandFilePath = (path: string, fileName: string): string => {
  return `${connection.base.storage.url}/storage-files/preview/file${path}/${fileName}`;
};

export const getBrandFileUrlByPathAndFileName = (
  path: string,
  fileName: string,
): string => {
  return `${connection.base.storage.url}/storage-files/preview/file${path}/${fileName}`;
};

/**
 * Turn whatever a stored `url` field happens to hold into something safe to put in a
 * `src` / `href`, or `null` when it is not previewable.
 *
 * Three cases only:
 *  - an absolute `http(s)` URL  → used as-is;
 *  - a storage path (`/storage-files/…`, or the same string with the leading slash
 *    missing — a well-known storage-path defect, see the storage-path conventions)
 *    → prefixed with the storage base URL, so it resolves instead of 404-ing
 *      against the app origin;
 *  - anything else → `null`.
 *
 * The third case is the point: it is a WHITELIST, so an authored `javascript:` /
 * `data:` / `vbscript:` value can never reach an attribute. The scheme test is
 * anchored and case-insensitive, and a protocol-relative `//host/x` is rejected too
 * (it would silently inherit the page scheme and leave the origin).
 */
export const normalizeStoragePreviewUrl = (
  rawUrl?: string | null,
): string | null => {
  if (typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  if (url.length === 0) return null;
  // A relative storage path is about to be prefixed onto the storage origin, so ".." would let a
  // stored value walk to an arbitrary path there once the browser resolves it. Rejected before the
  // scheme test so it also cannot ride in on a path that merely looks absolute.
  if (url.includes("..")) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // A storage path, with or without its leading slash. getBrandFileUrlByPathAndFileName
  // requires the slash, so re-add it when it is missing.
  if (url.startsWith("/storage-files/")) {
    return `${connection.base.storage.url}${url}`;
  }
  if (url.startsWith("storage-files/")) {
    return `${connection.base.storage.url}/${url}`;
  }
  return null;
};

export type DisplayFileType = StorageFileType | "FULL_WIDTH_IMAGE";

export const showBrandFileByPathAndFileName = (
  fileType: DisplayFileType,
  path: string,
  fileName: string,
  onLoad?: ReactEventHandler<HTMLElement>,
  onLoadStart?: ReactEventHandler<HTMLElement>,
): ReactNode => {
  if (fileType === "IMAGE") {
    return (
      <img
        className="stu-img"
        src={getBrandFilePath(path, fileName)}
        alt="file"
        onLoad={onLoad as ReactEventHandler<HTMLImageElement>}
        onLoadStart={onLoadStart as ReactEventHandler<HTMLImageElement>}
      />
    );
  } else if (fileType === "FULL_WIDTH_IMAGE") {
    return (
      <img
        className="stu-img-full"
        src={getBrandFilePath(path, fileName)}
        alt="file"
        onLoad={onLoad as ReactEventHandler<HTMLImageElement>}
        onLoadStart={onLoadStart as ReactEventHandler<HTMLImageElement>}
        crossOrigin="anonymous"
      />
    );
  } else if (fileType === "VIDEO") {
    return (
      <video
        src={getBrandFilePath(path, fileName)}
        controls
        onLoad={onLoad as ReactEventHandler<HTMLVideoElement>}
        onLoadStart={onLoadStart as ReactEventHandler<HTMLVideoElement>}
      />
    );
  } else if (fileType === "AUDIO") {
    return (
      <audio
        src={getBrandFilePath(path, fileName)}
        controls
        onLoad={onLoad as ReactEventHandler<HTMLAudioElement>}
        onLoadStart={onLoadStart as ReactEventHandler<HTMLAudioElement>}
      />
    );
  } else if (fileType === "PDF") {
    return (
      <Suspense fallback={null}>
        <PDFPreview
          file={getBrandFilePath(path, fileName)}
          width={300}
          onLoad={onLoad as unknown as () => void}
        />
      </Suspense>
    );
  }
};

export const showFileByFileTypeAndUrl = (
  fileType: StorageFileType,
  url: string,
): ReactNode => {
  if (fileType === "IMAGE") {
    return url ? (
      <img
        src={url}
        alt="file"
        style={{ objectFit: "contain" }}
      />
    ) : null;
  } else if (fileType === "VIDEO") {
    return (
      <video
        src={url}
        controls
      />
    );
  } else if (fileType === "AUDIO") {
    return (
      <audio
        src={url}
        controls
      />
    );
  } else if (fileType === "PDF") {
    return (
      <Suspense fallback={null}>
        <PDFPreview file={url} width={300} />
      </Suspense>
    );
  }
};

export interface WebpConversionOptions {
  // 0..1 encode quality. Defaults per source mime: PNG 0.92, JPEG 0.82.
  quality?: number;
  // Optional longest-edge cap in px (aspect-preserving). Default: no downscale.
  maxDimension?: number;
  // Keep the original File when the WebP is not smaller. Default: true.
  keepSmaller?: boolean;
}

// Re-encode a raster image File to WebP on the client (via canvas.toBlob). Best-effort and
// NON-throwing: anything that is not a convertible raster (PNG/JPEG), or any failure, returns
// the ORIGINAL File so an upload is never blocked by conversion. SVG (would rasterize away
// the vectors), GIF (would drop animation), already-WebP, and non-images all pass through
// untouched. Quality defaults by source mime — PNG 0.92 (keeps text/diagram edges crisp),
// JPEG 0.82 (already lossy). The keepSmaller guard keeps the original when WebP cannot beat
// it. NOTE: canvas WebP is always lossy; true lossless would require a WASM libwebp encoder.
export const convertImageFileToWebp = async (
  file: File,
  options: WebpConversionOptions = {},
): Promise<File> => {
  try {
    if (!file || !file.type) {
      return file;
    }
    const type = file.type.toLowerCase();
    const isPng = type === "image/png";
    const isJpeg = type === "image/jpeg" || type === "image/jpg";
    if (!isPng && !isJpeg) {
      return file;
    }

    const quality =
      options.quality != null ? options.quality : isPng ? 0.92 : 0.82;
    const keepSmaller = options.keepSmaller !== false;
    const maxDimension = options.maxDimension;

    // Load pixels. createImageBitmap is fast and honors EXIF orientation for phone photos;
    // fall back to <img> + object URL where it is unavailable.
    let width: number;
    let height: number;
    let source: CanvasImageSource;
    let cleanup = () => {};
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      } as any);
      width = bitmap.width;
      height = bitmap.height;
      source = bitmap;
      cleanup = () => bitmap.close();
    } else {
      const objectUrl = URL.createObjectURL(file);
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = reject;
        element.src = objectUrl;
      });
      width = image.naturalWidth;
      height = image.naturalHeight;
      source = image;
      cleanup = () => URL.revokeObjectURL(objectUrl);
    }

    let targetWidth = width;
    let targetHeight = height;
    if (maxDimension && Math.max(width, height) > maxDimension) {
      const scale = maxDimension / Math.max(width, height);
      targetWidth = Math.round(width * scale);
      targetHeight = Math.round(height * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      cleanup();
      return file;
    }
    // No background fill — preserves PNG alpha (WebP supports it).
    context.drawImage(source, 0, 0, targetWidth, targetHeight);
    cleanup();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", quality),
    );
    if (!blob) {
      return file;
    }
    if (keepSmaller && blob.size >= file.size) {
      return file;
    }

    const baseName = file.name ? file.name.replace(/\.[^.]+$/, "") : "";
    return new File([blob], `${baseName || "image"}.webp`, {
      type: "image/webp",
    });
  } catch {
    return file;
  }
};

// Mutate a FormData in place: convert every File under the "file" key to WebP. Mutating the
// same instance (delete + re-append) keeps callers that hold the reference valid, and touches
// only the "file" key so fileType/path/userId/etc. are left exactly as-is. When nothing is
// convertible the FormData is left byte-identical.
export const convertFormDataImageFilesToWebp = async (
  formData: any,
  options?: WebpConversionOptions,
): Promise<void> => {
  if (!formData || typeof formData.getAll !== "function") {
    return;
  }
  const files = formData
    .getAll("file")
    .filter(
      (entry: any) => typeof File !== "undefined" && entry instanceof File,
    );
  if (files.length === 0) {
    return;
  }
  const converted = await Promise.all(
    files.map((file: File) => convertImageFileToWebp(file, options)),
  );
  if (converted.every((result, index) => result === files[index])) {
    return;
  }
  formData.delete("file");
  converted.forEach((file) => formData.append("file", file));
};

// Re-export types referenced by callers that previously inferred them from JS.
export type { AcceptedFormat, FileExtension };

// The media kind to render ONE option value with.
//
// An activity declares a single contentMap.type for its whole material map, so every
// option had to be the same kind: all audio, or all images, or all text. A language
// listening item wants them mixed - match a spoken clip to a picture, put a recording
// beside a written word - which that single type cannot say. Declaring TEXT so the mix
// validates then renders every media URL as its own literal address on screen.
//
// The value itself can say it, and already does elsewhere: OpenEndedActivityMaterialContent
// sniffs the learner's answer the same way, because the declared type and the real one
// disagree in practice.
//
// So: trust the value when it is an http(s) URL whose extension names a media kind, and
// fall back to the activity's declared type otherwise - which keeps every existing
// single-kind activity rendering exactly as before, prose options included.
export const resolveMaterialMediaType = (
  value: unknown,
  declaredContentType: string,
) => {
  if (typeof value !== "string") {
    return declaredContentType;
  }
  const trimmedValue = value.trim();
  if (!/^https?:\/\//i.test(trimmedValue)) {
    return declaredContentType;
  }
  // A fragment ends the path just as a query does, so both have to go before the extension is
  // read: ".../a.mp3#t=30" otherwise sniffs as the extension "mp3#t=30" and falls back to the
  // declared type - which is the wrong-element-for-the-file case this resolver exists to stop.
  const cutIndex = Math.min(
    ...["?", "#"]
      .map((delimiter) => trimmedValue.indexOf(delimiter))
      .filter((index) => index >= 0)
      .concat(trimmedValue.length),
  );
  const pathOnly = trimmedValue.slice(0, cutIndex);
  const extension = (pathOnly.split(".").pop() || "").toLowerCase();
  const sniffedType = retrieveFileTypeFromExtension(extension);
  if (sniffedType === "IMAGE" || sniffedType === "AUDIO" || sniffedType === "VIDEO") {
    return sniffedType;
  }
  return declaredContentType;
};
